import React, { useEffect } from 'react';
import { Crisp } from 'crisp-sdk-web';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import tw from 'twin.macro';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import { useTranslation } from 'react-i18next';
import Link from '@teetsh/app/src/components/Link';
import { routes } from 'containers/routes';
import Button from 'components/Buttons/Button';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { useIsBelowBreakpoint } from 'components/ResponsiveSwitcher/useIsBelowSize';
import { TeetshBackground } from '@teetsh/app/src/components/TeetshBackground';
import Logo from 'assets/logo-black@2x.png';

// trigger build
interface Props {
  children: React.ReactNode;
  floating?: boolean;
  withLogoOnLargerScreens?: boolean;
  withLoginLink?: boolean;
  withSignupLink?: boolean;
  small?: boolean;
  large?: boolean;
}

const LINK_STYLE = [
  tw`ml-1 font-semibold underline text-reverse-intense`,
  { textUnderlineOffset: '4px' },
];

export function OnboardingWrapper(props: Props) {
  const {
    floating,
    withLogoOnLargerScreens,
    withLoginLink,
    withSignupLink,
    small,
    large,
  } = props;
  const { t } = useTranslation(['auth', 'onboarding']);
  const isMobile = useIsBelowBreakpoint('md');
  const location = useLocation();

  useEffect(() => {
    if (isMobile && !withLogoOnLargerScreens && Crisp.isCrispInjected()) {
      Crisp.chat.hide();
    }
    return Crisp.isCrispInjected() ? () => Crisp.chat.show() : undefined;
  }, [isMobile, withLogoOnLargerScreens, Crisp.isCrispInjected()]);

  return (
    <TeetshBackground>
      {/* HEADER */}
      <div
        css={[
          tw`w-full px-6 py-4 flex items-center`,
          withLogoOnLargerScreens
            ? tw`pt-10 pb-8 md:py-14 lg:py-16 [@media(max-height:800px)]:py-5`
            : tw`md:pt-10 lg:pt-16 [@media(max-height:800px)]:pt-4`,
          floating ? tw`justify-center` : tw`justify-between md:justify-center`,
        ]}
      >
        <img
          css={[
            tw`h-10 [@media(max-height:800px)]:h-8`,
            withLogoOnLargerScreens
              ? tw`h-[60px] lg:h-[80px] [@media(max-height:800px)]:h-[60px]`
              : tw`md:hidden`,
          ]}
          alt="Teetsh"
          src={Logo}
        />
        {!withLogoOnLargerScreens && Crisp.isCrispInjected() && (
          <Button
            onClick={() => {
              Crisp.chat.show();
              Crisp.chat.open();
            }}
            tw="flex md:hidden text-white"
            outlined
            icon={QuestionMarkCircleIcon}
          >
            {t('onboarding:help')}
          </Button>
        )}
        {(withLoginLink || withSignupLink) && (
          <div
            css={[
              tw`absolute top-10 right-10 text-sm text-reverse-intense`,
              floating && tw`hidden lg:block`,
            ]}
          >
            {withLoginLink && (
              <>
                {t('onboarding:signup.alreadyHaveAnAccount')}
                <Link css={LINK_STYLE} to={`/login${location.search}`}>
                  {t('onboarding:signup.signin')}
                </Link>
              </>
            )}
            {withSignupLink && (
              <>
                {t('auth:signin.noAccountQuestion')}&nbsp;
                <Link
                  className="test-create-account"
                  css={LINK_STYLE}
                  to={`${routes.inscription}${location.search}`}
                >
                  {t('auth:signin.createAccountLink')}
                </Link>
              </>
            )}
          </div>
        )}
      </div>
      {/* MAIN CONTENT */}
      <div
        css={[
          tw`md:w-[720px] md:pb-12 lg:pb-16 md:[@media(max-height:800px)]:pb-8`,
          floating
            ? tw`px-4 pb-4 [&>div]:rounded-lg md:px-0`
            : tw`grow md:grow-0 overflow-y-hidden`,
          small && tw`md:max-w-md`,
          large && tw`lg:w-full lg:max-w-5xl lg:px-8`,
        ]}
      >
        {props.children}
      </div>
      {/* FOOTER */}
      {(withLoginLink || withSignupLink) && (
        <div tw="pt-4 pb-8 text-sm text-reverse-intense lg:hidden">
          {withLoginLink && (
            <>
              {t('onboarding:signup.alreadyHaveAnAccount')}
              <Link css={LINK_STYLE} to={`/login${location.search}`}>
                {t('onboarding:signup.signin')}
              </Link>
            </>
          )}
          {withSignupLink && (
            <>
              {t('auth:signin.noAccountQuestion')}
              <Link
                className="test-create-account"
                css={LINK_STYLE}
                to={`${routes.inscription}${location.search}`}
              >
                {t('auth:signin.createAccountLink')}
              </Link>
            </>
          )}
        </div>
      )}
    </TeetshBackground>
  );
}

export function DemarrageWrapper() {
  const user = useUser();
  const { search, pathname } = useLocation();
  const urlSearchParams = new URLSearchParams(search);
  const isAddTeacherMode = urlSearchParams.get('mode') === 'add-teacher-role';

  if (
    user.hasFinishedOnboarding !== undefined &&
    user.hasFinishedOnboarding &&
    !isAddTeacherMode
  ) {
    // this has to be before any other condition to make sure the user is redirected if he already onboarded
    return <Navigate to={{ pathname: '/dashboard', search }} />;
  }

  return (
    <OnboardingWrapper
      floating={pathname === '/demarrage/bienvenue'}
      withLogoOnLargerScreens={[
        '/demarrage/bienvenue',
        '/demarrage/preparation',
        '/demarrage/final',
      ].includes(pathname)}
    >
      <Outlet />
    </OnboardingWrapper>
  );
}
